import React from 'react';
import Label from '../../components/ui/label';
import { useDispatch } from 'react-redux';
import { updateField } from '../../features/it-user-forms/user-forms-slice';
import ButtonDropdownOption from '../../components/ui/button-dropdown-option';
import Input from '../../components/ui/input';
import { Spinner, UserIcon } from '../../assets/icon/svg';
import { useCreateUser } from '../../hooks/actions/use-create-user';
import { CreateAccountFormState } from '../../features/it-user-forms/user-form-state';
import { customStyle, defaultStyle } from '../../constant/custom-style';
import ConfirmDefaultModal from '../../components/global/modal/confirm-default-modal';
import { useUserManagementDataState } from '../../hooks/data-state/use-user-management-data-state';

export default function CreateAccountPage() {
    
    const dispatch = useDispatch();
    const {
        showConfirmModal,
        setShowConfirmModal,
        handleCreateAccountClick,
        handleSubmit,
        handleClear,
        
        isFetchingDepartment,
        isFetchingRoleType,
        isFetchingLocationType,
        isFetchingReportingHeadType,
        isLoadingCreateAccount,
        
        openDropdown,
        setOpenDropdown,

        suffixOptions,
        roleOptions,
        departmentOptions,
        locationOptions,
        reportingHeadOptions,
    } = useCreateUser()
    const {
        firstName,
        middleName,
        lastName,
        suffix,
        email,
        position,
        agentCode,
        role,
        roleId,
        location,
        department,
        phoneNumber,
        reportingTo,
    } = useUserManagementDataState()

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        if (name === "phoneNumber") {
            dispatch(updateField({ field: name as keyof CreateAccountFormState, value }));
        }
        dispatch(updateField({ field: name as keyof CreateAccountFormState, value }));
    };

    // if fieldName is role and it change the value, could you reset the value of reporting_to_id to null, because the reporting head is dependent on the role selected. If the role changes, the reporting head should be reset to null.
    const DropdownField = ({ label, value, options, fieldName, isLoading, isSubTitle }: { label: string; value: any; options: any[]; fieldName: keyof CreateAccountFormState, isLoading: boolean, isSubTitle: boolean }) => (
        <div className=''>
            <Label 
                label={label} 
                requiredText={`${fieldName === 'reporting_to_id' ? '' : '*'}`}
                fontSize="text-xs mb-1"
                isRequired={true}
            />
            <ButtonDropdownOption
                isLoading={isLoading}
                width="w-full"
                padding="px-3 py-2" 
                options={options}
                isSubTitle={isSubTitle}
                value={value}
                open={openDropdown === fieldName}
                setOpen={() => setOpenDropdown(openDropdown === fieldName ? null : fieldName)}
                onChange={(option) => {
                    dispatch(updateField({ field: fieldName, value: option }));
                    if (fieldName === 'role') {
                        dispatch(updateField({
                            field: 'reporting_to_id',
                            value: { id: 0, name: "Select" }
                        }));
                    }
                }}
            />
            {fieldName === 'reporting_to_id' && (
                <p className="mt-1 text-[10px] text-gray-400">
                    Reporting Head is required for the role of Corporate Financial Executive in the Marketing Officer, GMS Support & Training department.
                </p>
            )}
        </div>
    );
    
    return (
        <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-[#f5f9fd] rounded-xl border border-gray-200/80">
            <div className="sticky top-0 z-20 flex-shrink-0 border-b border-gray-200 bg-white shadow-sm">
                <div className="rounded-t-xl bg-[#186cb2] px-4 py-3 sm:px-5">
                    <div className="flex items-center gap-3">
                        <div>
                            <h1 className="text-sm font-semibold text-white">
                                Create Account
                            </h1>
                            <p className="mt-0.5 text-[11px] text-blue-100 sm:text-xs">
                                Fill in the employee details to create a new account.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            {/* CONTENT */}
            <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="w-full px-3 py-3">
                    <div className="space-y-4">
                        <section className="rounded-xl border border-gray-200 bg-white">
                            <div className="border-b rounded-t-xl border-gray-100 bg-gray-50 px-4 py-3">
                                <div className="flex items-start gap-2.5">
                                    <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-[#186cb2]/10 text-[#186cb2]">
                                        <UserIcon width={15} height={15} />
                                    </div>
                                    <div>
                                        <p className="text-xs font-bold text-gray-800">
                                            Personal Information
                                        </p>
                                        <p className="text-[10px] text-gray-400">
                                            Enter the employee's basic personal and contact information.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                                <div>
                                    <Label
                                        label="First Name"
                                        fontSize="text-xs mb-1"
                                        requiredText=" *"
                                        isRequired={true}
                                    />
                                    <Input
                                        type="text"
                                        name="firstname"
                                        value={firstName}
                                        isCustomStyle={true}
                                        customCss={`${defaultStyle} ${ !firstName ? '' : customStyle}`}
                                        onChange={handleChange}
                                        required={true}
                                        placeholder="Enter first name"
                                        disabled={isLoadingCreateAccount}
                                    />
                                </div>
                                <div>
                                    <Label
                                        label="Middle Name"
                                        fontSize="text-xs mb-1"
                                        // requiredText=" *"
                                        // isRequired={true}
                                    />
                                    <Input
                                        type="text"
                                        name="middlename"
                                        value={middleName}
                                        isCustomStyle={true}
                                        customCss={`${defaultStyle} ${ !middleName ? '' : customStyle}`}
                                        onChange={handleChange}
                                        required={true}
                                        placeholder="Enter middle name"
                                        disabled={isLoadingCreateAccount}
                                    />
                                </div>
                                <div>
                                    <Label
                                        label="Last Name"
                                        fontSize="text-xs mb-1"
                                        requiredText=" *"
                                        isRequired={true}
                                    />
                                    <Input
                                        type="text"
                                        name="lastname"
                                        value={lastName}
                                        isCustomStyle={true}
                                        customCss={`${defaultStyle} ${ !lastName ? '' : customStyle}`}
                                        onChange={handleChange}
                                        required={true}
                                        placeholder="Enter last name"
                                        disabled={isLoadingCreateAccount}
                                    />
                                </div>
                                <div>
                                    <Label
                                        label="Suffix"
                                        fontSize="text-xs mb-1"
                                    />
                                    <ButtonDropdownOption
                                        width="w-full" 
                                        padding="px-3 py-2" 
                                        options={suffixOptions} 
                                        isLoading={false} 
                                        value={suffix} 
                                        onChange={(option) => {
                                            dispatch(updateField({ field: 'suffix', value: option }));
                                        }}
                                        open={openDropdown === 'suffix'} 
                                        setOpen={() => setOpenDropdown(openDropdown === 'suffix' ? null : 'suffix')} 
                                    />
                                </div>
                                <div>
                                    <Label
                                        label="Position"
                                        fontSize="text-xs mb-1"
                                    />
                                    <Input
                                        type="text"
                                        name="position"
                                        value={position ?? ''}
                                        isCustomStyle={true}
                                        customCss={`${defaultStyle} ${ !position ? '' : customStyle}`}
                                        onChange={handleChange}
                                        required={true}
                                        placeholder="Enter position"
                                        disabled={isLoadingCreateAccount}
                                    />
                                </div>
                                <div>
                                    <Label
                                        label="Email"
                                        fontSize="text-xs mb-1"
                                        requiredText=" *"
                                        isRequired={true}
                                    />
                                    <Input
                                        type="text"
                                        name="email"
                                        value={email ?? ''}
                                        isCustomStyle={true}
                                        customCss={`${defaultStyle} ${ !email ? '' : customStyle}`}
                                        onChange={handleChange}
                                        required={true}
                                        placeholder="Enter middle name"
                                        disabled={isLoadingCreateAccount}
                                    />
                                </div>
                                <div>
                                    <Label
                                        label="Agent Code"
                                        fontSize="text-xs mb-1"
                                        requiredText=" *"
                                        isRequired={true}
                                    />
                                    <Input
                                        type="text"
                                        name="agent_code"
                                        value={agentCode ?? ''}
                                        isCustomStyle={true}
                                        customCss={`${defaultStyle} ${ !agentCode ? '' : customStyle}`}
                                        onChange={handleChange}
                                        required={true}
                                        placeholder="Enter middle name"
                                        disabled={isLoadingCreateAccount}
                                    />
                                </div>
                                <div className="sm:col-span-2 lg:col-span-1">
                                    <Label
                                        label="Mobile Number"
                                        fontSize="text-xs mb-1"
                                        requiredText=" *"
                                        isRequired={true}
                                    />
                                    <div
                                        className={`flex rounded border overflow-hidden focus-within:border-[#186cb2] border-gray-300 ${
                                            phoneNumber.trim() !== ''
                                                ? ''
                                                : ''
                                        }`}
                                    >
                                        <ButtonDropdownOption
                                            isShowArrow={false}
                                            isDisable={false}
                                            width="w-auto"
                                            padding="px-2 py-[6px] rounded-l rounded-r-none border-none"
                                            options={[]}
                                            isLoading={false}
                                            value={{id: 1, name: '+63'}}
                                            onChange={(option) => {}}
                                            open={false}
                                            isNoBorder={true}
                                            setOpen={() =>{}}
                                        />
                                        <Input
                                            type="text"
                                            name="phoneNumber"
                                            value={phoneNumber}
                                            isCustomStyle={true}
                                            customCss={`rounded-tr rounded-br w-full text-xs px-2 py-1.5 outline-none ${phoneNumber.trim() === '' ? '' : customStyle }`}
                                            placeholder="9123456789"
                                            required={true}
                                            onChange={handleChange}
                                            maxLength={10}
                                        />
                                    </div>
                                    <p className="mt-1 text-[10px] text-gray-400">
                                        Enter your 10-digit mobile number without +63.
                                    </p>
                                </div>
                            </div>
                        </section>
                        <section className="rounded-xl border border-gray-200 bg-white">
                            <div className="border-b rounded-t-xl border-gray-100 bg-gray-50 px-4 py-3 ">
                                <div className="flex items-start gap-2.5">
                                    <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-[#186cb2]/10 text-[#186cb2]">
                                        <UserIcon width={15} height={15} />
                                    </div>
                                    <div>
                                        <p className="text-xs font-bold text-gray-800">
                                            Account Information
                                        </p>
                                        <p className="text-[10px] text-gray-400">
                                            Enter the employee's work information.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                                <DropdownField 
                                    isLoading={isFetchingRoleType} 
                                    label="Role " 
                                    value={role} 
                                    options={roleOptions} 
                                    fieldName="role" 
                                    isSubTitle={false}
                                />
                                <DropdownField 
                                    isLoading={isFetchingLocationType} 
                                    label="Location " 
                                    value={location} 
                                    options={locationOptions} 
                                    fieldName="location" 
                                    isSubTitle={false}
                                />
                                <DropdownField 
                                    isLoading={isFetchingDepartment} 
                                    label="Department " 
                                    value={department} 
                                    options={departmentOptions} 
                                    fieldName="department" 
                                    isSubTitle={false}
                                />
                                <DropdownField 
                                    isLoading={isFetchingReportingHeadType} 
                                    label="Reporting Head " 
                                    value={reportingTo} 
                                    options={reportingHeadOptions} 
                                    fieldName="reporting_to_id" 
                                    isSubTitle={true}
                                />
                                    
                                {/* {(roleId === ROLE_IDs.corporateFinancialExecutive 
                                || roleId === ROLE_IDs.teamLeader
                            ) && (
                                    <DropdownField 
                                        isLoading={isFetchingReportingHeadType} 
                                        label="Reporting Head " 
                                        value={reportingTo} 
                                        options={reportingHeadOptions} 
                                        fieldName="reporting_to_id" 
                                        isSubTitle={true}
                                    />
                                )} */}
                            </div>
                        </section>
                    </div>
                </div>
            </div>
            <div className="flex flex-col-reverse gap-2 bg-gray-50/70 px-3 py-3 sm:flex-row sm:justify-end sm:px-4">
                <button
                    type="button"
                    onClick={handleClear}
                    disabled={isLoadingCreateAccount}
                    className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 12a9 9 0 1 0 3-6.7" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 4v6h6" />
                    </svg>
                    <span>Reset</span>
                </button>
                <button
                    type="button"
                    onClick={handleCreateAccountClick}
                    disabled={isLoadingCreateAccount}
                    className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#186cb2] px-4 py-2 text-xs font-semibold text-white shadow-sm shadow-[#186cb2]/20 transition hover:bg-[#155d9b] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-[#186cb2]/20 disabled:cursor-not-allowed disabled:bg-gray-400 disabled:opacity-70 sm:w-auto"
                >
                    {isLoadingCreateAccount ? (
                        <>
                            <Spinner size={13} />
                            <span>Creating Account...</span>
                        </>
                    ) : (
                        <>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5">
                                <circle cx="9" cy="8" r="3" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.5 19a5.5 5.5 0 0 1 11 0M19 8v6M16 11h6" />
                            </svg>
                            <span>Create Account</span>
                        </>
                    )}
                </button>
            </div>
            {showConfirmModal && (
                <ConfirmDefaultModal
                    modalTitle="Confirm Account Creation"
                    modalSubTitle="Are you sure you want to create this account? Please make sure all employee information is correct before continuing."
                    onCancel={() => setShowConfirmModal(false)}
                    onConfirm={handleSubmit}
                    isLoading={isLoadingCreateAccount}
                />
            )}
        </div>
    );
}